import { twMerge } from 'tailwind-merge'
import * as React from 'react'
import cx from 'classnames'
import { type DefaultProps } from '../common/types'

export interface CheckboxProps extends DefaultProps, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  disabled?: boolean
  defaultChecked?: boolean
  helperText?: string
  size?: 'default' | 'small'
  isPII?: boolean
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      testId,
      defaultChecked,
      ariaLabel,
      children,
      disabled,
      helperText,
      size = 'default',
      id,
      isPII,
      ...props
    },
    ref,
  ) => {
    const checkboxId = id ?? `checkbox-${new Date().getTime()}`

    const baseClass = cx(
      'cursor-pointer checked:after:absolute checked:after:ml-[7px] checked:after:mt-px checked:after:top-px checked:after:block checked:after:h-3 checked:after:w-[5px] checked:after:rotate-45 checked:after:border-[1.5px] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-borders-focus checked:after:content-[""] peer w-5 h-5 rounded relative appearance-none shrink-0 border border-borders-dark bg-white checked:border-borders-focus disabled:border-borders-dark disabled:after:border-borders-dark mt-0.5',
      {
        'mt-0': size === 'small',
        'fs-exclude': isPII,
      },
    )

    const finalClasses = twMerge(baseClass, className)

    return (
      <span className="relative flex items-start gap-3">
        <input
          type="checkbox"
          id={checkboxId}
          disabled={disabled}
          {...(testId ? { 'data-testid': testId } : {})}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          className={finalClasses}
          defaultChecked={defaultChecked}
          ref={ref}
          {...props}
        />

        {children ? (
          <label
            className={cx('typo-primary-base-regular text-text-body flex flex-col w-full', {
              'typo-primary-sm-regular': size === 'small',
              'cursor-pointer': !disabled,
              'text-borders-dark text-text-disabled pointer-events-none': disabled,
            })}
            htmlFor={checkboxId}
          >
            {children}
            {helperText ? (
              <span
                className={cx('typo-primary-sm-light mt-1', {
                  'typo-primary-xs-light': size === 'small',
                })}
              >
                {helperText}
              </span>
            ) : null}
          </label>
        ) : null}
      </span>
    )
  },
)

Checkbox.displayName = 'Checkbox'

export { Checkbox }
